import React from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useFrame } from '../../utils/useFrameP1'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { Html } from '@react-three/drei';
import FastLine from './FastLine';
import { CrosshairProps } from '../../types';
import { appProfiler } from '../../utils/appProfiler';

const CrosshairRenderer = React.forwardRef<any, CrosshairProps>(({ interactionMode, width, currentPoints3D, data, floorY, topY, startX, axisX, wallZ, frontZ, xformRef }, ref) => {
  const indexRef = React.useRef(0);
  const lastFormattedIdx = React.useRef(-1);
  const cachedTimeStr = React.useRef("");
  const groupRef = React.useRef<THREE.Group>(null);
  const ballRef = React.useRef<THREE.Mesh>(null);
  const glowRef = React.useRef<THREE.Mesh>(null);
  
  const floorLineRef = React.useRef<any>(null);
  const wallLineRef = React.useRef<any>(null);
  const depthLineRef = React.useRef<any>(null);
  
  const priceGroupRef = React.useRef<THREE.Group>(null);
  const timeGroupRef = React.useRef<THREE.Group>(null);
  const priceHtmlRef = React.useRef<HTMLDivElement>(null);
  const timeHtmlRef = React.useRef<HTMLDivElement>(null);

  React.useImperativeHandle(ref, () => ({
    setIndex: (i: number) => {
      indexRef.current = i;
    }
  }));

  const { pointer, camera, raycaster } = useThree();
  const hitPlane = React.useMemo(() => new THREE.Plane(new THREE.Vector3(0, 0, 1), -wallZ), [wallZ]);
  const intersectPoint = React.useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    appProfiler.time('useFrame:CrosshairRenderer', () => {
    if (!groupRef.current) return;
    
    if (interactionMode !== 'crosshair' || !currentPoints3D.length || !data.length) { 
       groupRef.current.visible = false;
       if (priceHtmlRef.current) priceHtmlRef.current.style.display = 'none';
       if (timeHtmlRef.current) timeHtmlRef.current.style.display = 'none';
       return;
    }

    raycaster.setFromCamera(pointer, camera);
    const hit = raycaster.ray.intersectPlane(hitPlane, intersectPoint);
    if (!hit) {
       groupRef.current.visible = false;
       if (priceHtmlRef.current) priceHtmlRef.current.style.display = 'none';
       if (timeHtmlRef.current) timeHtmlRef.current.style.display = 'none';
       return;
    }

    const x = intersectPoint.x;
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(((x - startX) / width) * (data.length - 1))));
    indexRef.current = idx;
    
    groupRef.current.visible = true;
    if (priceHtmlRef.current) priceHtmlRef.current.style.display = 'block';
    if (timeHtmlRef.current) timeHtmlRef.current.style.display = 'block';

    let crossPoint = { x: 0, y: 0, z: 0 };
    if (currentPoints3D instanceof Float32Array) {
      const cpIdx = (idx * 3 < currentPoints3D.length) ? (idx * 3) : 0;
      crossPoint = { x: currentPoints3D[cpIdx], y: currentPoints3D[cpIdx+1], z: currentPoints3D[cpIdx+2] };
    } else {
      const p = currentPoints3D[idx] || currentPoints3D[0];
      if (p) {
        crossPoint = { x: p.x, y: p.y, z: p.z };
      }
    }
    const crossData = data[idx] || data[0];

    if (!crossPoint || !crossData) return;
    // Escala ao vivo: o ponto vem da geometria-base do lote principal
    const X = xformRef?.current;
    if (X && X.ready) {
      crossPoint.y = X.mainA * crossPoint.y + X.mainC;
      crossPoint.z = (X.mainZK ?? 1) * crossPoint.z;
    }

    if (ballRef.current) ballRef.current.position.set(crossPoint.x, crossPoint.y, crossPoint.z);
    if (glowRef.current) glowRef.current.position.set(crossPoint.x, crossPoint.y, crossPoint.z);
    
    if (floorLineRef.current?.geometry) {
       const attr = floorLineRef.current.geometry.getAttribute('position');
       if (attr) {
         attr.setXYZ(0, crossPoint.x, floorY, crossPoint.z);
         attr.setXYZ(1, crossPoint.x, topY, crossPoint.z);
         attr.needsUpdate = true;
       }
    }
    if (wallLineRef.current?.geometry) {
       const attr = wallLineRef.current.geometry.getAttribute('position');
       if (attr) {
         attr.setXYZ(0, startX, crossPoint.y, crossPoint.z);
         attr.setXYZ(1, axisX, crossPoint.y, crossPoint.z);
         attr.needsUpdate = true;
       }
    }
    if (depthLineRef.current?.geometry) {
       const attr = depthLineRef.current.geometry.getAttribute('position');
       if (attr) {
         attr.setXYZ(0, crossPoint.x, crossPoint.y, wallZ);
         attr.setXYZ(1, crossPoint.x, crossPoint.y, frontZ);
         attr.needsUpdate = true;
       }
    }
    
    if (priceGroupRef.current) {
       priceGroupRef.current.position.set(axisX, crossPoint.y, wallZ + 2);
    }
    if (priceHtmlRef.current) {
       priceHtmlRef.current.innerText = '$' + (crossData.price?.toFixed(2) ?? '0.00');
    }
    
    if (timeGroupRef.current) {
       timeGroupRef.current.position.set(crossPoint.x, floorY + 2, frontZ + 30);
    }
    if (timeHtmlRef.current) {
        if (lastFormattedIdx.current !== idx) {
           let crossDateStr = "";
           if (crossData.timestamp) { // Real UNIX timestamp 
               const d = new Date(crossData.timestamp);
              crossDateStr = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
           } else {
              const crossDate = new Date();
              crossDate.setDate(crossDate.getDate() - (data.length - 1 - idx));
              crossDateStr = crossDate.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) + ' ' + crossDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
           }
           cachedTimeStr.current = crossDateStr;
           lastFormattedIdx.current = idx;
        }
        timeHtmlRef.current.innerText = cachedTimeStr.current;
    }
    });
  });

  return (
    <group ref={groupRef} visible={false}>
      {/* The chosen point ball */}
      <mesh ref={ballRef}>
        <sphereGeometry args={[2, 16, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      <mesh ref={glowRef}>
        <sphereGeometry args={[4, 16, 16]} />
        <meshBasicMaterial color="#00d9ff" transparent depthWrite={false} opacity={0.4} />
      </mesh>

      {/* Cross Lines */}
      {/* To Time axis (Floor) */}
      <FastLine ref={floorLineRef} points={[0,0,0, 0,1,0]} color="#00d9ff" lineWidth={1} dashed dashSize={2} gapSize={2} opacity={0.8} transparent depthWrite={false} />
      {/* To Price axis (Back Wall) */}
      <FastLine ref={wallLineRef} points={[0,0,0, 1,0,0]} color="#00d9ff" lineWidth={1} dashed dashSize={2} gapSize={2} opacity={0.8} transparent depthWrite={false} />
      {/* To Momentum axis (Z depth) */}
      <FastLine ref={depthLineRef} points={[0,0,0, 0,0,1]} color="#00d9ff" lineWidth={1} dashed dashSize={2} gapSize={2} opacity={0.8} transparent depthWrite={false} />

      {/* Price Label (On Back Wall) */}
      <group ref={priceGroupRef}>
        <Html transform scale={40}>
          <div ref={priceHtmlRef} className="bg-[#00d9ff]/90 backdrop-blur-md border border-white text-white px-2 py-1 rounded text-[10px] font-bold font-mono shadow-[0_0_15px_rgba(0,217,255,0.5)] pointer-events-none whitespace-nowrap" style={{ transform: 'translateY(-50%)' }}>
            $0.00
          </div>
        </Html>
      </group>
      
      {/* Time Label (On Floor) */}
      <group ref={timeGroupRef} rotation={[-Math.PI / 2, 0, 0]}>
        <Html transform scale={40}>
          <div ref={timeHtmlRef} className="bg-[#00d9ff] border-2 border-white text-[#0a0e27] px-3 py-1.5 rounded-md text-[11px] font-black font-mono shadow-[0_0_20px_rgba(0,217,255,0.7)] pointer-events-none whitespace-nowrap">
            00/00/0000 00:00
          </div>
        </Html>
      </group>
    </group>
  );
});

export default CrosshairRenderer;
